import React, { useState, useEffect, useRef } from 'react';
import '../../css/ResultsData.css';
import pencilIcon from './assets/pencil.png';


const styles = {
  zoneDropdownContainer: {
    fontSize: 14,
    position: 'relative',
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column'
  },
  zoneLabel: {
    fontWeight: 'bold',
    marginRight: 8,
    alignItems: 'center',
    color: '#839299'
  },
  zoneDropdown: {
    fontSize: 14,
    cursor: 'pointer',
    backgroundColor: '#eff0f0',
    color: '#AAA',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  arrowContainer: {
    backgroundColor: '#aaa',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  selectedZoneStyle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    boxSizing: 'border-box',
    textOverflow: 'ellipsis',
    maxWidth: '100px'
  },
  arrowSvg: {
    width: "100%",
    height: "100%",
    fill: 'white',
    transition: 'transform 0.2s ease',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  dropdownList: {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    textOverflow: 'ellipsis',
    backgroundColor: '#bbc0c2',
    zIndex: 9999,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    padding: '4px 0',
    position: 'fixed',
    marginTop: '24px',
    overflow: 'auto',
    maxHeight: '140px'
  },
  dropdownItem: {
    padding: '6px 10px',
    color: '#425158',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownItemHover: {
    backgroundColor: '#028a90',
  },
  zoneNameText: {
    flexGrow: 1,
    userSelect: 'none',
  },
  zoneOptionInput: {
    width: 120,
    fontSize: 14,
    padding: 4,
    boxSizing: 'border-box',
  },
  editDeleteButtons: {
    display: 'flex',
    gap: 4,
    marginLeft: 8,
  },
  button: {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: 'black',
    fontSize: 12,
    lineHeight: 1,
    padding: '0 3px',
    userSelect: 'none',
  },
  addSuggestion: {
    fontStyle: 'italic',
    opacity: 0.8,
  },
  searchInputContainer: {
    padding: '6px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
  },
  searchInput: {
    width: '100%',
    fontSize: 14,
    padding: 6,
    boxSizing: 'border-box',
    border: 'none',
    borderRadius: 16,
  },
};

const ZoneName2 = ({ li, cleanParsedInsData, setCleanParsedInsData, claimId }) => {
  if (!li) return <div style={styles.zoneDropdownContainer}></div>;

  const key = Object.keys(li)[0];
  const item = li[key];
  const detectedZone = item?.detectedZone;
  const userModifiedZone = item?.userModifiedZone;
  const currentZone = userModifiedZone || detectedZone || 'unknown';

  const detectedZonesObj = cleanParsedInsData?.detectedZones ?? {};
  const userModifiedZonesObj = cleanParsedInsData?.userModifiedZones ?? {};

  const systemZoneNames = Object.keys(detectedZonesObj);
  const userZoneNames = Object.keys(userModifiedZonesObj);

  const hasUserModifications =
    JSON.stringify(userZoneNames.sort()) !== JSON.stringify(systemZoneNames.sort());
  const effectiveZonesObj = hasUserModifications ? userModifiedZonesObj : detectedZonesObj;

  const [selectedZone, setSelectedZone] = useState(currentZone);
  const [isOpen, setIsOpen] = useState(false);
  const [zonesObj, setZonesObj] = useState(effectiveZonesObj);
  const [editingZones, setEditingZones] = useState({});
  const [hoveredZone, setHoveredZone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setZonesObj(effectiveZonesObj);
  }, [cleanParsedInsData?.userModifiedZones, cleanParsedInsData?.detectedZones]);

  useEffect(() => {
    const newItem = cleanParsedInsData?.lineItems?.find((obj) => obj[key]);
    const updatedZone = newItem?.[key]?.userModifiedZone || newItem?.[key]?.detectedZone || 'unknown';
    setSelectedZone(updatedZone);
  }, [cleanParsedInsData, key]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setEditingZones({});
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveToBackend = async (updatedData) => {
    if (!claimId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_ZONE2_UPDATE}/${claimId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cleanParsedInsData: updatedData }),
      });
      if (!response.ok) throw new Error('Failed to save zone update');
    } catch (err) {
      console.error('❌ Error saving zone data:', err);
    }
  };

  const handleZoneChange = (newZone) => {
    setSelectedZone(newZone);
    setIsOpen(false);
    setSearchTerm('');
    setEditingZones({});

    const updatedData = { ...cleanParsedInsData };
    for (const obj of updatedData.lineItems || []) {
      if (obj[key]) obj[key].userModifiedZone = newZone;
    }

    updatedData.userModifiedZones = updatedData.userModifiedZones || {};
    updatedData.userModifiedZones[newZone] = true;

    setCleanParsedInsData(updatedData);
    saveToBackend(updatedData);
  };

  const startEditZone = (zone) => setEditingZones((prev) => ({ ...prev, [zone]: zone }));
  const editZoneChange = (zone, val) => setEditingZones((prev) => ({ ...prev, [zone]: val }));

  const saveZoneEdit = (oldZone) => {
    const newZone = editingZones[oldZone]?.trim();
    if (!newZone || newZone === oldZone) {
      setEditingZones((prev) => {
        const copy = { ...prev };
        delete copy[oldZone];
        return copy;
      });
      return;
    }

    const updatedZones = { ...zonesObj };
    delete updatedZones[oldZone];
    updatedZones[newZone] = true;

    const updatedData = { ...cleanParsedInsData, userModifiedZones: updatedZones };
    for (const obj of updatedData.lineItems || []) {
      for (const lineKey in obj) {
        if (obj[lineKey].userModifiedZone === oldZone) {
          obj[lineKey].userModifiedZone = newZone;
        }
      }
    }

    setCleanParsedInsData(updatedData);
    saveToBackend(updatedData);
    setZonesObj(updatedZones);
    setEditingZones((prev) => {
      const copy = { ...prev };
      delete copy[oldZone];
      return copy;
    });
  };

  const handleZoneDelete = (zoneToDelete) => {
    const updatedZones = { ...zonesObj };
    delete updatedZones[zoneToDelete];

    const updatedData = { ...cleanParsedInsData, userModifiedZones: updatedZones };
    for (const obj of updatedData.lineItems || []) {
      for (const lineKey in obj) {
        if (obj[lineKey].userModifiedZone === zoneToDelete) {
          obj[lineKey].userModifiedZone = 'unknown';
        }
      }
    }

    setCleanParsedInsData(updatedData);
    saveToBackend(updatedData);
    setZonesObj(updatedZones);
  };

  const handleAddZone = (zoneName) => {
    const trimmed = zoneName?.trim();
    if (!trimmed || zonesObj[trimmed]) {
      setSearchTerm('');
      return;
    }

    const updatedZones = { ...zonesObj, [trimmed]: true };
    const updatedData = { ...cleanParsedInsData, userModifiedZones: updatedZones };

    setZonesObj(updatedZones);
    setCleanParsedInsData(updatedData);
    saveToBackend(updatedData);
    setSelectedZone(trimmed);
    setSearchTerm('');
    setIsOpen(false);
  };

  const filteredZones = Object.keys(zonesObj).filter((z) =>
    z.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exactMatch = Object.keys(zonesObj).some(
    (z) => z.toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const toggleDropdown = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      setDropdownCoords({
        width: 'auto',
      });
    }
    setIsOpen((o) => !o);
  };

  return (
    <div className="responsive-flex">
      <label style={styles.zoneLabel}>ZONE:</label>
      <div title={selectedZone} style={styles.zoneDropdownContainer} ref={dropdownRef}>
        <div
          style={styles.zoneDropdown}
          onClick={toggleDropdown}
          ref={triggerRef}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleDropdown();
            } else if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          role="combobox"
        >
          <span style={styles.selectedZoneStyle}>{selectedZone}</span>
          <div style={styles.arrowContainer}>
            <svg
              style={{ ...styles.arrowSvg, ...(isOpen ? styles.arrowOpen : {}) }}
              viewBox="0 0 24 24"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div
            style={{
              ...styles.dropdownList,
              top: dropdownCoords.top,
              left: dropdownCoords.left,
              width: dropdownCoords.width,
            }}
            role="listbox"
            tabIndex={-1}
          >
            <div style={styles.searchInputContainer}>
              <input
                type="text"
                placeholder="Search or add zone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !exactMatch && searchTerm.trim()) {
                    e.preventDefault();
                    handleAddZone(searchTerm);
                  }
                }}
                autoFocus
                style={styles.searchInput}
              />
            </div>

            {!exactMatch && searchTerm.trim() !== '' && (
              <div
                style={{ ...styles.dropdownItem, ...styles.addSuggestion }}
                onClick={() => handleAddZone(searchTerm)}
                tabIndex={0}
              >
                ➕ Add new zone: "{searchTerm.trim()}"
              </div>
            )}

            {filteredZones.map((zone) => {
              const isEditing = editingZones[zone] !== undefined;
              const isSelected = selectedZone === zone;
              return (
                <div
                  key={zone}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => !isEditing && handleZoneChange(zone)}
                  onMouseEnter={() => setHoveredZone(zone)}
                  onMouseLeave={() => setHoveredZone(null)}
                  style={{
                    ...styles.dropdownItem,
                    ...(hoveredZone === zone ? styles.dropdownItemHover : {}),
                    fontWeight: isSelected ? 'bold' : 'normal',
                  }}
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editingZones[zone]}
                        onChange={(e) => editZoneChange(zone, e.target.value)}
                        onBlur={() => saveZoneEdit(zone)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveZoneEdit(zone);
                          if (e.key === 'Escape') {
                            setEditingZones((prev) => {
                              const copy = { ...prev };
                              delete copy[zone];
                              return copy;
                            });
                          }
                        }}
                        autoFocus
                        style={styles.zoneOptionInput}
                      />
                      <div style={styles.editDeleteButtons}>
                        <button onClick={() => handleZoneDelete(zone)} style={styles.button}>
                          REMOVE
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span
                        style={styles.zoneNameText}
                        title="Click zone name to select"
                        onClick={() => handleZoneChange(zone)}
                      >
                        {zone}
                      </span>
                      <div style={styles.editDeleteButtons}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditZone(zone);
                          }}
                          style={styles.button}
                        >
                          <img src={pencilIcon} alt="Pencil Icon" style={{ height: '16px', width: 'auto' }} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleZoneDelete(zone);
                          }}
                          style={styles.button}
                        >
                          REMOVE
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {!Object.keys(zonesObj).includes('unknown') && (
              <div
                role="option"
                tabIndex={0}
                aria-selected={selectedZone === 'unknown'}
                onClick={() => handleZoneChange('unknown')}
                style={{
                  ...styles.dropdownItem,
                  ...(hoveredZone === 'unknown' ? styles.dropdownItemHover : {}),
                  fontWeight: selectedZone === 'unknown' ? 'bold' : 'normal',
                }}
              >
                <span style={styles.zoneNameText}>unknown</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneName2;
